import classNames from 'classnames';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SetLogo } from '@sorare/core/src/atoms/icons/SetLogo';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useMyCardsContext } from '#src/contexts/myCards/index.tsx';

import commonBaseSetLogo from './assets/common-base-set.webp';
import stellarLogo from './assets/stellar.webp';
import winterLogo from './assets/winter.webp';

const ASSETS = {
  winter: { logo: winterLogo, borderColor: '#07F3D0' },
  stellar: { logo: stellarLogo, borderColor: '#07F3D0' },
  'common-base-set': {
    logo: commonBaseSetLogo,
    borderColor: 'var(--c-nd-500)',
  },
};

const Root = styled.div`
  width: 100%;
  @media ${laptopAndAbove} {
    width: unset;
  }
`;

const Item = styled(ButtonBase)`
  padding: var(--double-unit) var(--quadruple-unit);
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--double-unit);
  opacity: 0.3;
  transition: opacity 0.2s ease-in-out;
  border: 1px solid transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  &:hover,
  &:focus {
    opacity: 0.7;
  }
  &.active {
    opacity: 1;
  }
  @media ${laptopAndAbove} {
    padding: var(--quadruple-unit);
  }
`;

const IconWrapper = styled.div`
  & img {
    object-fit: contain;
  }
`;

type Props = {
  cardSetSlug: string;
  path: (cardSetSlug: string) => string;
  className?: string;
};

const hasKey = <T extends Record<string, unknown>>(
  obj: T,
  key: string
): key is Extract<keyof T, string> => key in obj;

const SET_ASSETS = {
  stellar: {
    Icon: () => (
      <SetLogo
        style={{
          height: 'var(--double-unit)',
        }}
      />
    ),
  },
  winter: {
    Icon: () => (
      <SetLogo
        style={{
          height: 'var(--double-unit)',
        }}
      />
    ),
  },
  'common-base-set': {
    Icon: ({ cardSet }: { cardSet: { name: string } }) => (
      <BrandXS className="text-center">{cardSet.name}</BrandXS>
    ),
  },
};

export const CollectionSetSwitcher = ({
  cardSetSlug,
  path,
  className,
}: Props) => {
  const { cardSets } = useMyCardsContext();
  const isLaptop = useIsLaptop();
  if (cardSets.length <= 1) {
    return null;
  }
  return (
    <Root className={className}>
      <Scrollable
        itemToDisplay={isLaptop ? cardSets.length : 2}
        itemGap={1}
        overhang={0}
      >
        {cardSets
          .map(cardSet => {
            const currentCardSetSlug = cardSet.slug;
            if (!hasKey(ASSETS, currentCardSetSlug)) return null;
            const { Icon } = hasKey(SET_ASSETS, currentCardSetSlug)
              ? SET_ASSETS[currentCardSetSlug]
              : { Icon: null };

            return (
              <Item
                key={currentCardSetSlug}
                to={path(currentCardSetSlug)}
                replace
                className={classNames({
                  active: currentCardSetSlug === cardSetSlug,
                })}
                style={{
                  backgroundImage: `url(${ASSETS[currentCardSetSlug].logo})`,
                  borderColor: ASSETS[currentCardSetSlug].borderColor,
                }}
              >
                <IconWrapper>
                  {!!Icon && <Icon cardSet={cardSet} />}
                </IconWrapper>
              </Item>
            );
          })
          .filter(Boolean)}
      </Scrollable>
    </Root>
  );
};
